import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';
import Integrations from './Integrations';
import AISettings from './AISettings';
import './Settings.css';

const Settings = () => {
  const [customCriteria, setCustomCriteria] = useState('');
  const userId = localStorage.getItem('userId'); // Ensure userId is stored after login
  const [surveyTitle, setSurveyTitle] = useState(''); // New state for survey title
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const [userData, setUserData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isEditingUser, setIsEditingUser] = useState(false); // Toggle for user info editing
  const [message, setMessage] = useState(null);
  const [publicCalendarLink, setPublicCalendarLink] = useState('');
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [timezone, setTimezone] = useState(moment.tz.guess()); // Default to visitor's timezone
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [publicSurveyLink, setPublicSurveyLink] = useState('');
  const [isPublicCalendarExpanded, setPublicCalendarExpanded] = useState(false);
  const [aiTonePreference, setAiTonePreference] = useState('professional');
  const [writingSamples, setWritingSamples] = useState([]);
const [aiCustomPrompt, setAiCustomPrompt] = useState('');
const [isAiTrainingExpanded, setAiTrainingExpanded] = useState(false);
const [isGoogleSyncExpanded, setGoogleSyncExpanded] = useState(false);
const [isCalendarSubscribeExpanded, setCalendarSubscribeExpanded] = useState(false);
const [calendarFeedUrl, setCalendarFeedUrl] = useState('');
const [feedCopied, setFeedCopied] = useState(false);
const [isCustomAnalysisExpanded, setCustomAnalysisExpanded] = useState(false);
const [isSurveyExpanded, setSurveyExpanded] = useState(false);
const [isAccountInfoExpanded, setAccountInfoExpanded] = useState(false); // Default to expanded
const [isWorkHoursExpanded, setIsWorkHoursExpanded] = useState(false);
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [deleteConfirmText, setDeleteConfirmText] = useState('');
const [deletingAccount, setDeletingAccount] = useState(false);
const timezones = Intl.supportedValuesOf('timeZone');
const [modalVisible, setModalVisible] = useState(false);
const [modalType, setModalType] = useState(''); // 'success' or 'error'
const [isMultipleChoice, setIsMultipleChoice] = useState(false);
const [newOption, setNewOption] = useState('');
const [options, setOptions] = useState([]);
const [modalMessage, setModalMessage] = useState('');
  const navigate = useNavigate();
  const { hash } = useLocation();

  // Auto-expand section based on URL hash (e.g. /settings#integrations)
  useEffect(() => {
    if (hash === '#integrations') {
      setPublicCalendarExpanded(true);
      setGoogleSyncExpanded(true);
      setCalendarSubscribeExpanded(true);
      setTimeout(() => {
        document.getElementById('integrations-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    if (hash === '#ai-settings') {
      setAiTrainingExpanded(true);
      setCustomAnalysisExpanded(true);
      setTimeout(() => {
        document.getElementById('ai-settings-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [hash]);
  const [businessHours, setBusinessHours] = useState({
    monday: { start: '', end: '', isDayOff: false },
    tuesday: { start: '', end: '', isDayOff: false },
    wednesday: { start: '', end: '', isDayOff: false },
    thursday: { start: '', end: '', isDayOff: false },
    friday: { start: '', end: '', isDayOff: false },
    saturday: { start: '', end: '', isDayOff: false },
    sunday: { start: '', end: '', isDayOff: false },
  });
  const [isbusinessHoursExpanded, setIsbusinessHoursExpanded] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

// Update fetching user settings to include businessHours
// Update fetching user settings to include businessHours
useEffect(() => {
  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authorization token found. Please log in again.');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch user data including custom criteria, user details, and business hours
      const response = await axios.get(`${apiUrl}/api/users/me`, { headers });

      // Set user data
      setUserData({
        firstname: response.data.firstname || '',
        lastname: response.data.lastname || '',
        email: response.data.email || '',
        password: '', // Password will be empty initially
        confirmPassword: '', // Confirm password will also be empty initially
      });

      // Set custom criteria
      setCustomCriteria(response.data.customCriteria || '');

      // ✅ Set AI personalization fields
      setAiTonePreference(response.data.tonePreference || 'professional');
      setAiCustomPrompt(response.data.customPrompts || '');
      setWritingSamples(response.data.writingSamples || []); // ✅ Add this line

      // Set business hours
      if (response.data.businessHours) {
        setBusinessHours(response.data.businessHours);
      }

      if (response.data.timezone) {
        setTimezone(response.data.timezone); // Set the fetched timezone
      }

      console.log('User data:', response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };
  fetchSettings();
}, [apiUrl]);



const handleSaveAIPreferences = async () => {
  const token = localStorage.getItem('token');
  try {
    await axios.put(
      `${process.env.REACT_APP_API_URL}/api/users/me`,
      {
        tonePreference: aiTonePreference,
        customPrompts: aiCustomPrompt,
        writingSamples: writingSamples, // ✅ include this
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    showModal('success', 'AI preferences saved!');
  } catch (error) {
    console.error('Failed to save AI preferences:', error);
    console.log('🔍 Sending writingSamples:', writingSamples);
    showModal('error', 'Failed to save AI preferences.');
  }
};



useEffect(() => {
  const fetchGoogleAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/google/google-accounts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setGoogleAccounts(response.data.googleAccounts);
    } catch (error) {
      console.error('Error fetching Google accounts:', error);
    }
  };

  fetchGoogleAccounts();
}, []);

  // Show modal with a message and type
  const showModal = (type, message) => {
    setModalType(type);
    setModalMessage(message);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

// Handle toggling the "Day Off" checkbox
const handleDayOffToggle = (day) => {
  setBusinessHours((prevState) => ({
    ...prevState,
    [day]: {
      ...prevState[day],
      isDayOff: !prevState[day].isDayOff,
      start: !prevState[day].isDayOff ? '' : prevState[day].start,
      end: !prevState[day].isDayOff ? '' : prevState[day].end,
    },
  }));
};

// Function to save work hours
const handleDeleteAccount = async () => {
  if (deleteConfirmText !== 'DELETE') return;
  setDeletingAccount(true);
  try {
    const token = localStorage.getItem('token');
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/account`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    localStorage.clear();
    navigate('/login');
  } catch (error) {
    console.error('Error deleting account:', error);
    setMessage({ type: 'error', text: 'Failed to delete account. Please try again.' });
  } finally {
    setDeletingAccount(false);
    setShowDeleteConfirm(false);
  }
};

const handleSaveWorkHours = async () => {
  try {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    console.log('Saving work hours and timezone:', { businessHours, timezone }); // Log for debugging

    const response = await axios.put(
      `${apiUrl}/api/users/me`,
      { businessHours, timezone }, // Ensure `timezone` is sent
      { headers }
    );

    console.log('Work hours and timezone updated successfully:', response.data);
    showModal('success', 'Work hours and timezone updated successfully!');
  } catch (error) {
    console.error('Failed to update work hours and timezone:', error.response ? error.response.data : error);
    showModal('error', 'Failed to update work hours and timezone');
  }
};

const handleBusinessHoursChange = (day, field, value) => {
  setBusinessHours(prevState => ({
    ...prevState,
    [day]: {
      ...prevState[day],
      [field]: value,
    },
  }));
};
    
  // Log out the user by clearing the token and navigating to the login page
  const handleLogout = useCallback(() => {
    const confirmLogout = window.confirm('Are you sure you want to log out?');
    
    if (confirmLogout) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  }, [navigate]);

  // Function to save survey title
  const handleSaveSurveyTitle = async () => {
    if (!surveyTitle.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}/survey-title`,
        { surveyTitle },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      showModal('success', 'Survey title updated successfully!');
    } catch (error) {
      console.error('Failed to update survey title:', error.response ? error.response.data : error);
    }
  };

    // Fetch survey settings
    useEffect(() => {
      const fetchSurveySettings = async () => {
        try {
          const token = localStorage.getItem('token');
          const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/${userId}/survey-title`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setSurveyTitle(data.surveyTitle || '');
        } catch (error) {
          console.error('Failed to fetch survey title:', error);
        }
      };
  
      fetchSurveySettings();
    }, [userId]);
  

   // Fetch existing survey questions
   useEffect(() => {
    const fetchSurveyQuestions = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/${userId}/questions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setSurveyQuestions(data);
      } catch (error) {
        console.error('Failed to fetch survey questions:', error);
      }
    };

    fetchSurveyQuestions();
  }, []);

  const handleDeleteQuestion = async (questionId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/${userId}/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      // Update the state to remove the question
      setSurveyQuestions(prevQuestions => prevQuestions.filter(question => question._id !== questionId));
    } catch (error) {
      console.error('Failed to delete question:', error);
    }
  };

// Function to add a new question
const handleAddQuestion = async () => {
  if (!newQuestion.trim()) return;

  const newQuestionObj = {
    questionText: newQuestion,
    isMultipleChoice,
    options: isMultipleChoice ? options : [],
  };

  try {
    // Add the new question via API
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/users/${userId}/questions`,
      newQuestionObj,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }
    );

    // Re-fetch the updated list of questions to ensure the newly added question is displayed
    const { data: updatedQuestions } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/users/${userId}/questions`,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }
    );

    // Update the state with the new list of questions
    setSurveyQuestions(updatedQuestions);

    // Clear the input after adding
    setNewQuestion('');
    setIsMultipleChoice(false);
    setOptions([]);

    // Keep the collapsible form expanded after adding a question
    setSurveyExpanded(true);

  } catch (error) {
    console.error('Failed to add question:', error.response ? error.response.data : error);
  }
};

const displaySurveyQuestions = () => {
  return surveyQuestions.map((q, index) => (
    <div key={index}>
      <p>{q.text || "Default question text if missing"}</p>
      <button onClick={() => handleDeleteQuestion(q._id)}>Delete</button>
    </div>
  ));
};

const checkTokenExpiration = useCallback(() => {
  const token = localStorage.getItem('token');
  if (token) {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    // Check if the token is expired
    if (decodedToken.exp < currentTime) {
      handleLogout(); // Log out if the token has expired
    }
  } else {
    handleLogout(); // If no token, force logout
  }
}, [handleLogout]); // Add handleLogout as a dependency

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      const link = `${window.location.origin}/public-calendar/${userId}`;
      setPublicCalendarLink(link);
      // Generate calendar subscribe feed URL
      const feedUrl = `${process.env.REACT_APP_API_URL}/api/calendar-feed/${userId}.ics`;
      setCalendarFeedUrl(feedUrl);
    } else {
      console.error('UserId not found in localStorage');
    }
  }, []);

  useEffect(() => {
    checkTokenExpiration(); // Check token on load
  
    const intervalId = setInterval(() => {
      checkTokenExpiration(); // Check token every 60 seconds
    }, 60000);

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [checkTokenExpiration]); // Add checkTokenExpiration to the dependency array

  const copyPublicCalendarLink = () => {
    if (publicCalendarLink) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(publicCalendarLink)
          .then(() => {
            showModal('success', 'Public calendar link copied to clipboard!');
          })
          .catch((error) => {
            console.error('Error copying link:', error);
            showModal('error', 'Failed to copy the public calendar link. Please try again.');
          });
      } else {
        // Fallback for browsers that do not support navigator.clipboard
        const tempInput = document.createElement('input');
        tempInput.value = publicCalendarLink;
        document.body.appendChild(tempInput);
        tempInput.select();
        try {
          document.execCommand('copy');
          showModal('success', 'Public calendar link copied to clipboard!');
        } catch (err) {
          console.error('Fallback: Unable to copy', err);
          showModal('error', 'Failed to copy the public calendar link using fallback method.');
        }
        document.body.removeChild(tempInput);
      }
    }
  };
  

  const [customSurveyCriteria, setCustomSurveyCriteria] = useState('');

// Fetch user settings including custom survey criteria on mount
useEffect(() => {
  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showModal('error' , 'No authorization token found. Please log in again.');
      navigate('/login');
      return;
    }

    try {
      // Fetch user details
      const userDetailsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserData({
        firstname: userDetailsResponse.data.firstname || '',
        lastname: userDetailsResponse.data.lastname || '',
        email: userDetailsResponse.data.email || '',
        // Don't fetch password and confirm password
      });

      // Fetch custom survey criteria
      const criteriaResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/${userId}/survey-criteria`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomSurveyCriteria(criteriaResponse.data.customSurveyCriteria || '');
    } catch (error) {
      console.error('Error fetching settings:', error);
      showModal('error' , 'Failed to fetch settings: ' + (error.response ? error.response.data.message : error.message));
    }
  };

  fetchSettings();
}, [navigate, userId]); // Include all necessary dependencies

// Function to save custom survey criteria
const handleSaveCustomSurveyCriteria = async () => {
  const token = localStorage.getItem('token');
  if (!customSurveyCriteria.trim()) {
    showModal('error' , 'Survey criteria cannot be empty.');
    return;
  }
  try {
    await axios.put(
      `${process.env.REACT_APP_API_URL}/api/users/${userId}/survey-criteria`,
      { customSurveyCriteria },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    showModal('success' , 'Survey criteria saved successfully!');
  } catch (error) {
    console.error('Failed to save survey criteria:', error);
    showModal('error' , 'Failed to save survey criteria: ' + error.response.data.message);
  }
};

  const handleSaveCustomCriteria = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/settings/custom-criteria`,
        { customCriteria },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      showModal('success' , 'Custom criteria saved successfully!');
    } catch (error) {
      console.error('Failed to save custom criteria:', error);
    }
  };

// Update `handleSaveUserData` to save businessHours as well
const handleSaveUserData = async () => {
  const { firstname, lastname, email, password, confirmPassword } = userData;

  if (password && password !== confirmPassword) {
    showModal('error' , 'Passwords do not match');

  }

  console.log("Saving user data and business hours:", { firstname, lastname, email, password, businessHours });

  const token = localStorage.getItem('token');
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/users/me`,
      { firstname, lastname, email, password, businessHours,timezone }, // Send the data including businessHours
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    showModal('success' , 'User details and work hours saved successfully!');
    showModal('success' , 'Changes saved!');
    setIsEditingUser(false); // Collapse the form after saving
    console.log("Save response:", response.data);
  } catch (error) {
    console.error('Failed to save user data:', error);
    showModal('success' , 'Failed to save changes');
  }
};

  const handleUserDataChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };

// Existing useEffect for setting the link
useEffect(() => {
  const userId = localStorage.getItem('userId');
  if (userId) {
    const publicSurveyLink = `${window.location.origin}/public-survey/${userId}`;
    setPublicSurveyLink(publicSurveyLink); // Set the public survey link
  }
}, []);

// Function to copy the public survey link
const copyPublicSurveyLink = async () => {
  if (publicSurveyLink) {
    try {
      await navigator.clipboard.writeText(publicSurveyLink);
      showModal('success', 'Public survey link copied to clipboard!');
    } catch (error) {
      console.error('Error copying link:', error);
      showModal('error', 'Failed to copy the public survey link. Please try again.');
    }
  }
};

  // Sync with Google Calendar handler
  const handleSyncClick = () => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      window.location.href = `${process.env.REACT_APP_API_URL}/api/google/auth/google?userId=${userId}`;
    } else {
      showModal('error' , 'User ID is missing. Please log in again.');
      console.error('User ID is not found in local storage');
    }
  };

  // Function to remove the sync with Google Calendar
  const handleRemoveSync = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/google/remove-sync`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      showModal('success', response.data.message);
      console.log('Google Calendar sync removed successfully');
    } catch (error) {
      console.error('Failed to remove Google Calendar sync:', error);
      showModal('error', 'Failed to remove Google Calendar sync. Please try again.');
    }
  };

  // Fetch profile photo on mount
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/profile-photo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.profilePhotoUrl) {
          setProfilePhotoUrl(response.data.profilePhotoUrl);
        }
      } catch (error) {
        console.error('Failed to fetch profile photo:', error);
      }
    };
    fetchProfilePhoto();
  }, []);

  // Upload profile photo
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showModal('error', 'Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showModal('error', 'Image must be under 5MB.');
      return;
    }

    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('photo', file);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/users/profile-photo`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setProfilePhotoUrl(response.data.profilePhotoUrl);
      showModal('success', 'Profile photo updated!');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      showModal('error', 'Failed to upload photo. Please try again.');
    }
    setUploadingPhoto(false);
  };

  // Remove profile photo
  const handleRemovePhoto = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/profile-photo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfilePhotoUrl(null);
      showModal('success', 'Profile photo removed.');
    } catch (error) {
      console.error('Failed to remove photo:', error);
      showModal('error', 'Failed to remove photo.');
    }
  };


  return (
    <div className="settings-container">

      {/* Header */}
      <div className="settings-header">
        <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
        <h2>Settings</h2>
        <p className="settings-subtitle">Manage your account and preferences</p>
      </div>

      {/* ── ACCOUNT INFORMATION ── */}
      <h3
        className="collapsible-title"
        onClick={() => setAccountInfoExpanded(!isAccountInfoExpanded)}
      >
        <span>Account information</span>
        <span>{isAccountInfoExpanded ? '▴' : '▾'}</span>
      </h3>
      {isAccountInfoExpanded && (
        <div className="user-settings">

          {/* Profile Photo */}
          <div className="profile-photo-section">
            <div className="profile-photo-wrapper">
              {profilePhotoUrl ? (
                <div className="profile-photo-container">
                  <img src={profilePhotoUrl} alt="Profile" className="profile-photo-img" />
                  <button className="profile-photo-remove" onClick={handleRemovePhoto} title="Remove photo">✕</button>
                </div>
              ) : (
                <div className="profile-photo-placeholder" onClick={() => photoInputRef.current?.click()}>
                  <span className="profile-photo-initials">
                    {(userData.firstname?.[0] || '').toUpperCase()}{(userData.lastname?.[0] || '').toUpperCase()}
                  </span>
                </div>
              )}
              <input
                type="file"
                ref={photoInputRef}
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
              <button
                className="profile-photo-upload-btn"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
              >
                {uploadingPhoto ? 'Uploading...' : profilePhotoUrl ? 'Change photo' : 'Upload photo'}
              </button>
            </div>
          </div>

          {/* Account Info Display */}
          <div className="account-info">
            <p><strong>First name:</strong> {userData.firstname}</p>
            <p><strong>Last name:</strong> {userData.lastname}</p>
            <p><strong>Email:</strong> {userData.email}</p>
          </div>

          <button className="settings-btn" onClick={() => setIsEditingUser(!isEditingUser)}>
            {isEditingUser ? 'Cancel' : 'Edit account information'}
          </button>

          {isEditingUser && (
            <div className="edit-user-form">
              <div className="form-group">
                <label>First name</label>
                <input type="text" name="firstname" value={userData.firstname} onChange={handleUserDataChange} className="settings-input" />
              </div>
              <div className="form-group">
                <label>Last name</label>
                <input type="text" name="lastname" value={userData.lastname} onChange={handleUserDataChange} className="settings-input" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" name="email" value={userData.email} onChange={handleUserDataChange} className="settings-input" />
              </div>
              <div className="form-group">
                <label>New password <span style={{fontWeight:300,fontSize:'0.85rem',color:'var(--text-light)'}}>optional</span></label>
                <input type="password" name="password" value={userData.password} onChange={handleUserDataChange} className="settings-input" />
              </div>
              <div className="form-group">
                <label>Confirm password</label>
                <input type="password" name="confirmPassword" value={userData.confirmPassword} onChange={handleUserDataChange} className="settings-input" />
              </div>
              {message && <p className="message">{message}</p>}
              <button className="save-button" onClick={handleSaveUserData}>Save changes</button>
            </div>
          )}
        </div>
      )}

      {/* ── INTEGRATIONS (imported component) ── */}
      <div id="integrations-section">
        <Integrations
          publicCalendarLink={publicCalendarLink}
          copyPublicCalendarLink={copyPublicCalendarLink}
          isPublicCalendarExpanded={isPublicCalendarExpanded}
          setPublicCalendarExpanded={setPublicCalendarExpanded}
          isGoogleSyncExpanded={isGoogleSyncExpanded}
          setGoogleSyncExpanded={setGoogleSyncExpanded}
          handleSyncClick={handleSyncClick}
          handleRemoveSync={handleRemoveSync}
          googleAccounts={googleAccounts}
          isCalendarSubscribeExpanded={isCalendarSubscribeExpanded}
          setCalendarSubscribeExpanded={setCalendarSubscribeExpanded}
          calendarFeedUrl={calendarFeedUrl}
          feedCopied={feedCopied}
          setFeedCopied={setFeedCopied}
        />
      </div>

      {/* ── AI SETTINGS (imported component) ── */}
      <div id="ai-settings-section">
        <AISettings
          isAiTrainingExpanded={isAiTrainingExpanded}
          setAiTrainingExpanded={setAiTrainingExpanded}
          aiTonePreference={aiTonePreference}
          setAiTonePreference={setAiTonePreference}
          aiCustomPrompt={aiCustomPrompt}
          setAiCustomPrompt={setAiCustomPrompt}
          writingSamples={writingSamples}
          setWritingSamples={setWritingSamples}
          handleSaveAIPreferences={handleSaveAIPreferences}
          isCustomAnalysisExpanded={isCustomAnalysisExpanded}
          setCustomAnalysisExpanded={setCustomAnalysisExpanded}
          customCriteria={customCriteria}
          setCustomCriteria={setCustomCriteria}
          handleSaveCustomCriteria={handleSaveCustomCriteria}
          isSurveyExpanded={isSurveyExpanded}
          setSurveyExpanded={setSurveyExpanded}
          surveyTitle={surveyTitle}
          setSurveyTitle={setSurveyTitle}
          handleSaveSurveyTitle={handleSaveSurveyTitle}
          surveyQuestions={surveyQuestions}
          handleDeleteQuestion={handleDeleteQuestion}
          newQuestion={newQuestion}
          setNewQuestion={setNewQuestion}
          isMultipleChoice={isMultipleChoice}
          setIsMultipleChoice={setIsMultipleChoice}
          options={options}
          setOptions={setOptions}
          newOption={newOption}
          setNewOption={setNewOption}
          handleAddQuestion={handleAddQuestion}
          customSurveyCriteria={customSurveyCriteria}
          setCustomSurveyCriteria={setCustomSurveyCriteria}
          handleSaveCustomSurveyCriteria={handleSaveCustomSurveyCriteria}
          publicSurveyLink={publicSurveyLink}
          copyPublicSurveyLink={copyPublicSurveyLink}
          message={message}
        />
      </div>

      {/* ── WORK HOURS ── */}
      <div className="work-hours-section">
        <h3
          className="collapsible-title"
          onClick={() => setIsWorkHoursExpanded(!isWorkHoursExpanded)}
        >
          <span>Work hours</span>
          <span>{isWorkHoursExpanded ? '▴' : '▾'}</span>
        </h3>
        {isWorkHoursExpanded && (
          <div className="work-hours-content">
            {Object.entries(businessHours).map(([day, { start, end, isDayOff }]) => (
              <div key={day} className="work-hours-day">
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isDayOff}
                      onChange={() => handleDayOffToggle(day)}
                    />
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                    <span style={{ color: 'var(--text-light)', fontWeight: 300, fontSize: '0.85rem' }}>
                      {isDayOff ? '— day off' : ''}
                    </span>
                  </label>
                </div>
                {!isDayOff && (
                  <>
                    <div className="form-group">
                      <label>Start</label>
                      <input
                        type="time"
                        value={start}
                        onChange={(e) => handleBusinessHoursChange(day, 'start', e.target.value)}
                        className="settings-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>End</label>
                      <input
                        type="time"
                        value={end}
                        onChange={(e) => handleBusinessHoursChange(day, 'end', e.target.value)}
                        className="settings-input"
                      />
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Timezone — once, outside the day loop */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label>Timezone</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="settings-input"
              >
                <option value="" disabled>Select timezone</option>
                {timezones.map((tz, index) => (
                  <option key={index} value={tz}>{tz}</option>
                ))}
              </select>
            </div>

            <button className="save-button" onClick={handleSaveWorkHours}>
              Save work hours
            </button>
          </div>
        )}
      </div>

      {/* ── HELP & GUIDE ── */
      <div className="manage-section" style={{marginTop:'1.5rem', textAlign:'center'}}>
        <a href="/help" style={{fontSize:'0.9rem', color:'var(--accent)', fontWeight:600, textDecoration:'none'}}>📖 Help & Guide</a>
        <span style={{color:'var(--text-light)', margin:'0 0.75rem'}}>·</span>
        <a href="mailto:support@airflyp.com" style={{fontSize:'0.9rem', color:'var(--text-light)', textDecoration:'none'}}>Contact support</a>
      </div>

      /* ── DANGER ZONE ── */}
      <div className="danger-zone-section">
        <h3 className="danger-zone-title">Danger zone</h3>
        {!showDeleteConfirm ? (
          <button className="delete-account-btn" onClick={() => setShowDeleteConfirm(true)}>
            Delete my account
          </button>
        ) : (
          <div className="delete-confirm-box">
            <p className="delete-warning">
              ⚠️ This will permanently delete your account, all clients, sessions, transcriptions, and data. This cannot be undone.
            </p>
            <p className="delete-instruction">Type <strong>DELETE</strong> to confirm:</p>
            <input
              type="text"
              className="settings-input"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
            />
            <div className="delete-confirm-buttons">
              <button className="cancel-delete-btn" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}>
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
              >
                {deletingAccount ? 'Deleting...' : 'Permanently delete account'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className={`modal ${modalType === 'success' ? 'modal-success' : 'modal-error'}`}>
            <div className="modal-header">
              {modalType === 'success' ? 'Saved' : 'Error'}
            </div>
            <div className="modal-body">{modalMessage}</div>
            <div className="modal-footer">
              <button onClick={closeModal} className="modal-close-btn">OK</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Settings;